import Util from '../utils/util';

import { Modules } from '@kaetram/common/network';

import type { Bonuses, Enchantments, Stats } from '@kaetram/common/types/item';

const ENCHANTMENT_NAMES_ES: { [key: number]: string } = {
        [Modules.Enchantment.Bloodsucking]: 'Vampírico',
        [Modules.Enchantment.Critical]: 'Crítico',
        [Modules.Enchantment.Evasion]: 'Evasión',
        [Modules.Enchantment.Thorns]: 'Espinas',
        [Modules.Enchantment.Explosive]: 'Explosivo',
        [Modules.Enchantment.Stun]: 'Aturdir',
        [Modules.Enchantment.AntiStun]: 'Anti-Aturdir',
        [Modules.Enchantment.Splash]: 'Salpicadura',
        [Modules.Enchantment.DoubleEdged]: 'Doble Filo'
    },
    STAT_LABELS_ES: { [key: string]: string } = {
        crush: 'Contundente',
        slash: 'Cortante',
        stab: 'Perforante',
        archery: 'Arquería',
        magic: 'Magia'
    },
    BONUS_LABELS_ES: { [key: string]: string } = {
        accuracy: 'Precisión',
        strength: 'Fuerza',
        archery: 'Arquería',
        magic: 'Magia'
    },
    ABILITY_NAMES_ES: { [key: string]: string } = {
        holyguard: 'Escudo Divino',
        shadowstrike: 'Golpe Sombrío'
    };

export interface ItemInfoData {
    key: string;
    name: string;
    count?: number;
    description?: string;
    equipmentType?: Modules.Equipment;
    attackStats?: Stats;
    defenseStats?: Stats;
    bonuses?: Bonuses;
    enchantments?: Enchantments;
    passiveAbility?: string;
    movementModifier?: number;
}

export default class ItemInfo {
    private container: HTMLElement = document.querySelector('#item-info')!;
    private itemImage: HTMLElement = document.querySelector('#item-info-image')!;
    private itemName: HTMLElement = document.querySelector('#item-info-name')!;
    private itemDesc: HTMLElement = document.querySelector('#item-info-desc')!;
    private statsBody: HTMLElement = document.querySelector('#item-info-stats')!;

    public constructor() {
        document.addEventListener('mousedown', (e) => {
            if (!this.container.contains(e.target as Node)) this.hide();
        });

        document.addEventListener('touchstart', (e) => {
            if (!this.container.contains(e.target as Node)) this.hide();
        });
    }

    public show(data: ItemInfoData, x: number, y: number): void {
        if (!data.key || !data.name) return;

        this.itemImage.style.backgroundImage = Util.getImageURL(data.key);
        this.itemName.textContent = data.name;

        let desc = data.description || '';
        if (data.count && data.count > 1)
            desc = desc ? `x${data.count} — ${desc}` : `x${data.count}`;
        this.itemDesc.textContent = desc;
        this.itemDesc.style.display = desc ? '' : 'none';

        this.statsBody.innerHTML = '';

        let isWeapon =
                data.equipmentType === Modules.Equipment.Weapon ||
                data.equipmentType === Modules.Equipment.WeaponSkin ||
                data.equipmentType === Modules.Equipment.Arrows,
            isDefensive =
                data.equipmentType === Modules.Equipment.Armour ||
                data.equipmentType === Modules.Equipment.ArmourSkin ||
                data.equipmentType === Modules.Equipment.Boots;

        // Armas/flechas → solo Ataque. Armaduras/skins/botas → solo Defensa.
        // Anillos, amuletos y alas son mixtos: se muestran ambas secciones si
        // el item trae stats en cada una.
        if (!isDefensive) this.addStatSection('Ataque', data.attackStats, STAT_LABELS_ES);
        if (!isWeapon) this.addStatSection('Defensa', data.defenseStats, STAT_LABELS_ES);
        if (isWeapon) this.addEnchantments(data.enchantments);
        this.addStatSection('Bonificaciones', data.bonuses, BONUS_LABELS_ES);
        if (!isWeapon) this.addEnchantments(data.enchantments);
        this.addPassiveInfo(data.passiveAbility, data.movementModifier);

        this.container.style.display = 'block';

        let rect = this.container.getBoundingClientRect(),
            vw = window.innerWidth,
            vh = window.innerHeight,
            left = x + 12,
            top = y - 12;

        if (left + rect.width > vw) left = x - rect.width - 12;
        if (top + rect.height > vh) top = vh - rect.height - 8;
        if (top < 4) top = 4;

        this.container.style.left = `${left}px`;
        this.container.style.top = `${top}px`;
    }

    public hide(): void {
        this.container.style.display = 'none';
    }

    private addStatSection(
        title: string,
        stats: Stats | Bonuses | undefined,
        labels: { [key: string]: string }
    ): void {
        if (!stats) return;

        let hasValue = false,
            section = document.createElement('div'),
            header = document.createElement('div');

        section.className = 'item-info-section';
        header.className = 'item-info-section-title';
        header.textContent = title;
        section.append(header);

        for (let [key, value] of Object.entries(stats as unknown as { [key: string]: number })) {
            let n = Number(value);
            if (!Number.isFinite(n) || n === 0) continue;
            hasValue = true;

            let row = document.createElement('div');
            row.className = 'item-info-row';

            let label = labels[key] ?? key,
                sign = n > 0 ? '+' : '',
                cls = n > 0 ? 'stat-positive' : 'stat-negative';
            row.innerHTML = `<span>${label}</span><span class="${cls}">${sign}${n}</span>`;
            section.append(row);
        }

        if (hasValue) this.statsBody.append(section);
    }

    private addPassiveInfo(passiveAbility?: string, movementModifier?: number): void {
        if (!passiveAbility && (!movementModifier || movementModifier <= 0)) return;

        let section = document.createElement('div'),
            header = document.createElement('div');

        section.className = 'item-info-section item-info-passive';
        header.className = 'item-info-section-title';
        header.textContent = 'Pasiva';
        section.append(header);

        if (passiveAbility) {
            let row = document.createElement('div');
            row.className = 'item-info-row';
            let name = ABILITY_NAMES_ES[passiveAbility] ?? passiveAbility;
            row.innerHTML = `<span>${name}</span><span class="stat-positive">Activa</span>`;
            section.append(row);
        }

        if (movementModifier && movementModifier > 0 && movementModifier < 1) {
            let pct = Math.round((1 - movementModifier) * 100),
                row = document.createElement('div');
            row.className = 'item-info-row';
            row.innerHTML = `<span>Velocidad</span><span class="stat-positive">+${pct}%</span>`;
            section.append(row);
        }

        this.statsBody.append(section);
    }

    private addEnchantments(enchantments?: Enchantments): void {
        if (!enchantments || Object.keys(enchantments).length === 0) return;

        let section = document.createElement('div'),
            header = document.createElement('div');

        section.className = 'item-info-section item-info-enchants';
        header.className = 'item-info-section-title';
        header.textContent = 'Encantamientos';
        section.append(header);

        let hasAny = false;

        for (let [id, data] of Object.entries(enchantments)) {
            let name = ENCHANTMENT_NAMES_ES[Number(id)] ?? `Enc. ${id}`,
                level = (data as { level: number })?.level ?? 0;
            if (level <= 0) continue;
            hasAny = true;

            let row = document.createElement('div');
            row.className = 'item-info-row';
            row.innerHTML = `<span>${name}</span><span class="enchant-level">Nv. ${level}</span>`;
            section.append(row);
        }

        if (hasAny) this.statsBody.append(section);
    }
}
